import {ComponentRef} from "@angular/core";
import {IModalOptions} from "./IModalOptions";

/**
 * Context of modal rendering
 */
export class ModalContext<T = any, TResult = any> {

  modalData: T;
  onDone: (result: TResult) => Promise<boolean>;
  onCancel: (reason?: any) => Promise<boolean>;

  private instanceAwaiters: { resolve: any, reject: any }[] = [];

  constructor(modalData: T, public options: IModalOptions) {
    this.modalData = modalData;
  }

  private _instance: ComponentRef<any>;

  get instance(): ComponentRef<any> {
    return this._instance;
  }

  private _destroyed = false;

  get destroyed(): boolean {
    return this._destroyed;
  }

  async done(result?: TResult) {
    const canDestroy = await this.onDone(result);
    if (canDestroy) {
      this.destroy();
    }
    return canDestroy;
  }

  async cancel(reason?: any) {
    const canDestroy = await this.onCancel(reason);
    if (canDestroy) {
      this.destroy();
    }
    return canDestroy;
  }

  destroy() {
    this._destroyed = true;
    this.destroyInstance();
  }

  ensureInstance(): Promise<ComponentRef<any>> {
    if (this._instance) {
      return Promise.resolve(this._instance);
    }
    return new Promise((resolve, reject) => {
      this.instanceAwaiters.push({resolve, reject});
    });
  }

  setComponentInstance(instance: ComponentRef<any>) {
    this._instance = instance;

    if (this._destroyed) {
      // modal was destroyed while component was instantiated
      this.destroyInstance();
      return;
    }

    if (this.instanceAwaiters.length) {
      for (const awaiter of this.instanceAwaiters) {
        awaiter.resolve(instance);
      }
      this.instanceAwaiters = [];
    }
  }

  private destroyInstance() {
    if (this.instance) {
      this.instance.destroy();
      this._instance = null;

      if (this.instanceAwaiters.length) {
        for (const awaiter of this.instanceAwaiters) {
          awaiter.reject();
        }
      }
    }
  }

}
